import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import type { ProjectActionResponse } from '@/api/v4/project-actions-info';
import { fetchProjectActionsInfo } from '@/api/v4/project-actions-info';
import { QueryKey } from '@/config/query-client';
import { useUserIdle } from '@/hooks/use-idle';

import { projectIdState } from '../project.state';
import { useAtomValue } from 'jotai';

const ACTION_PANEL_POLL_INTERVAL_MS = 10000;

export const useProjectActionsInfo = () => {
  const queryClient = useQueryClient();
  const projectId = useAtomValue(projectIdState);
  const isIdle = useUserIdle();
  const previousCountsRef = useRef<ProjectActionResponse | null>(null);

  useEffect(() => {
    previousCountsRef.current = null;
  }, [projectId]);

  const fetchActions = async () => {
    if (!projectId) return;

    return fetchProjectActionsInfo(projectId);
  };

  const query = useQuery({
    queryKey: [QueryKey.ProjectActionsInfo, projectId],
    queryFn: fetchActions,
    enabled: !!projectId,
    staleTime: 1000 * 60 * 5,
    refetchInterval:
      !!projectId && !isIdle ? ACTION_PANEL_POLL_INTERVAL_MS : false,
    retry: false,
  });

  const data = query.data;

  useEffect(() => {
    if (!data || !projectId) return;

    const previousCounts = previousCountsRef.current;
    previousCountsRef.current = data;

    if (!previousCounts) return;

    if (previousCounts.files !== data.files) {
      void queryClient.invalidateQueries({
        queryKey: [QueryKey.ProjectFiles, projectId],
      });
    }

    if (previousCounts.comments !== data.comments) {
      void queryClient.invalidateQueries({
        queryKey: [QueryKey.ProjectComments, projectId],
      });
    }

    if (previousCounts.meetings !== data.meetings) {
      void queryClient.invalidateQueries({
        queryKey: [QueryKey.ProjectMeetings, projectId],
      });
    }

    if (previousCounts.updates !== data.updates) {
      void queryClient.invalidateQueries({
        queryKey: [QueryKey.ProjectUpdates, projectId],
      });
    }
  }, [data, projectId, queryClient]);

  return query;
};
